import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ProfileRow {
  id: string;
  username: string | null;
  full_name: string | null;
  user_type: string | null;
  verification_status: string | null;
  city: string | null;
  country: string | null;
  subscription_status: string | null;
  bank_name: string | null;
  account_number: string | null;
  created_at: string | null;
  last_active_at: string | null;
  portal_id: string | null;
}

export interface OrderRow {
  id: string;
  total: number;
  currency: string;
  status: string;
  created_at: string;
}

const dayKey = (iso: string) => iso.slice(0, 10);

/** Last N days as ISO date keys, oldest first. */
export const dayRange = (days: number) => {
  const out: string[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
};

export const useAdminProfiles = () =>
  useQuery({
    queryKey: ["admin", "profiles"],
    queryFn: async (): Promise<ProfileRow[]> => {
      const { data, error } = await supabase
        .from("profiles")
        .select(
          "id, username, full_name, user_type, verification_status, city, country, subscription_status, created_at, last_active_at, portal_id",
        )
        .order("created_at", { ascending: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as ProfileRow[];
    },
    staleTime: 60_000,
  });

export const useAdminOrders = () =>
  useQuery({
    queryKey: ["admin", "orders"],
    queryFn: async (): Promise<OrderRow[]> => {
      const { data, error } = await supabase
        .from("orders")
        .select("id, total, currency, status, created_at")
        .order("created_at", { ascending: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as OrderRow[];
    },
    staleTime: 60_000,
  });

export const useAdminCounts = () =>
  useQuery({
    queryKey: ["admin", "counts"],
    queryFn: async () => {
      const tables = [
        "events",
        "tickets",
        "posts",
        "products",
        "bookings",
        "job_listings",
        "consultations",
        "payout_requests",
        "user_roles",
        "reviews",
      ] as const;
      const entries = await Promise.all(
        tables.map(async (t) => {
          const { count, error } = await supabase.from(t).select("id", { count: "exact", head: true });
          return [t, error ? 0 : (count ?? 0)] as const;
        }),
      );
      return Object.fromEntries(entries) as Record<(typeof tables)[number], number>;
    },
    staleTime: 60_000,
  });

/** Groups any timestamped rows into a per-day series for the last `days` days. */
export const toDailySeries = <T extends { created_at: string }>(
  rows: T[],
  days: number,
  value: (row: T) => number = () => 1,
) => {
  const buckets = new Map<string, number>(dayRange(days).map((d) => [d, 0]));
  rows.forEach((r) => {
    const k = dayKey(r.created_at);
    if (buckets.has(k)) buckets.set(k, (buckets.get(k) ?? 0) + value(r));
  });
  return Array.from(buckets, ([date, value]) => ({
    date,
    label: date.slice(5),
    value,
  }));
};

export const useEmailHealth = () =>
  useQuery({
    queryKey: ["admin", "email-health"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_send_log")
        .select("id, template_name, recipient_email, status, error_message, created_at")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });
